/**
 * PropertyPath — Investment Decision Tool
 * REDE Platform v2 · v3.9.0
 * 
 * Flow: Landing → Questionnaire → Dashboard (no discovery gate)
 * Discovery data is INFERRED from inputs, not asked upfront.
 * 
 * Routing:
 *   /           → Landing page (VHG default branding)
 *   /app        → Questionnaire directly (VHG default branding)
 *   /joe-mori   → Branded landing page → questionnaire → dashboard
 *   /app?s=...  → Shared analysis link
 *   /app?load=latest → Load most recent saved property
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { darkVars, lightVars } from './utils/theme';
import { brandHex } from './utils/reportPalette';
import { calculateHoldScenario, calculateSellScenario, calculate1031Scenario } from './utils/calculations';
import { NavBar, VHGFooter, ProGate, PrivacyPolicy, TermsOfService } from './components/UI';
import { setBrandPayload } from './utils/brandState';
import Questionnaire from './components/Questionnaire';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import { BRAND as DEFAULT_BRAND } from './utils/brand';

const RESERVED = ['app','api','docs','static','manifest.json','sw.js','icon.svg','icon-192.png','icon-512.png','favicon.ico','vault.html','rede-platform.js'];

function detectRoute() {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (!path) return { mode: 'landing', slug: null };
  if (path === 'app' || path.startsWith('app/')) return { mode: 'app', slug: null };
  if (RESERVED.includes(path) || path.includes('.')) return { mode: 'landing', slug: null };
  return { mode: 'agent', slug: path };
}

// Infer discovery-like data from questionnaire inputs
function inferDiscovery(data) {
  const isOwner = data.ownsProperty === 'yes';
  const appRate = parseFloat(data.annualAppreciation)||3;
  const altReturn = parseFloat(data.alternativeReturn)||7;
  const yearsToHold = parseInt(data.yearsToHold)||10;

  return {
    situation: isOwner ? (data.exitStrategy === '1031' ? "Exploring a 1031 exchange" : data.exitStrategy === 'sell' ? "Considering selling" : "Owner evaluating options") : "Evaluating a new purchase",
    situation_value: isOwner ? (data.exitStrategy||'undecided') : 'evaluating-purchase',
    priority: data.priority || '', // Set on AI tab
    risk: appRate >= 6 || altReturn >= 10 ? 'Aggressive — maximize returns, accept volatility' : appRate <= 2 ? 'Conservative — protect what I have' : 'Moderate — balanced growth',
    risk_value: appRate >= 6 || altReturn >= 10 ? 'aggressive' : appRate <= 2 ? 'conservative' : 'moderate',
    timeline: yearsToHold <= 3 ? 'Ready to act — next 6 months to 3 years' : yearsToHold <= 7 ? 'Exploring options — 1 to 7 years' : 'Long-term hold — 8+ years',
    timeline_value: yearsToHold <= 3 ? 'ready' : yearsToHold <= 7 ? 'exploring' : 'no-rush',
    experience: data.capRate ? 'Experienced investor' : 'General investor',
    experience_value: data.capRate ? 'experienced' : 'general',
    context: data.additionalContext || '', // "Anything else we should know?"
  };
}

/* ─── CROSS-APP PROMOTION + SHARE ──────────────────────────────────────
   Every REDE client app promotes two siblings. Links ALWAYS carry the agent
   slug when there is one. No entitlement check: plans are all-or-none, and
   carrying the slug for a lapsed agent is deliberate — the target app strips
   itself to house branding on its own, while REDE still counts the usage
   against that slug so the agent can be shown what they walked away from. */
const REDE_APPS = {
  strcalc:      { name:"STRcalc",       host:"strcalc",      icon:"\u{1F3E0}",
                  blurb:"Thinking about a short-term rental? Run the real Airbnb math — occupancy, seasonality, expenses and cash flow.",
                  short:"Run the real Airbnb math before you buy." },
  stroptimizer: { name:"STR Optimizer", host:"stroptimizer", icon:"\u{1F4C8}",
                  blurb:"Already running a short-term rental? Benchmark your pricing, amenities and listing against comparable rentals.",
                  short:"Benchmark the rental you already own." },
  sellercalc:   { name:"SellerCalc",    host:"sellercalc",   icon:"\u{1F4B0}",
                  blurb:"Thinking about selling? See your true net proceeds and what each post-sale path is actually worth.",
                  short:"Know your net proceeds before you list." },
  propertypath: { name:"PropertyPath",  host:"propertypath", icon:"\u{1F9ED}",
                  blurb:"Already own a property and not sure what to do with it? Model hold, sell, refinance or 1031 exchange side by side.",
                  short:"Hold, sell, refinance or 1031 — side by side." },
  rentorown:    { name:"RentOrOwn",     host:"rentorown",    icon:"\u{1F511}",
                  blurb:"Renting and wondering whether to buy? See which one actually comes out ahead, and when.",
                  short:"Rent or buy — see which one wins." },
};

function redeAppUrl(id, slug) {
  const a = REDE_APPS[id];
  if (!a) return "";
  return "https://" + a.host + ".redeapps.com" + (slug ? "/" + encodeURIComponent(slug) : "");
}

function CrossPromo({ targets, brandData, agentSlug, compact }) {
  const ids = (targets || []).filter((id) => !!REDE_APPS[id]);
  if (!ids.length) return null;
  const who = (brandData && brandData.brand_identity && brandData.brand_identity.name) || "";
  const tile = { display:'block', textDecoration:'none', background:'var(--card)', border:'1px solid var(--border)', borderLeft:'3px solid var(--accent)', borderRadius:12, padding: compact ? '14px 16px' : '18px 18px 16px', textAlign:'left' };
  return (
    <section style={{ padding: compact ? '26px 24px 30px' : '34px 24px 10px', borderTop: compact ? '1px solid var(--border)' : 'none' }}>
      <div style={{ maxWidth:860, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:16 }}>
          <div style={{ fontSize:11, letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--accent)', fontWeight:700, marginBottom:7 }}>
            {who ? `More from ${who}` : 'More free tools'}
          </div>
          <div style={{ fontSize: compact ? 18 : 21, fontWeight:800, color:'var(--text)' }}>
            {compact ? 'Keep going — two more free tools' : 'Two more free tools you can use right now'}
          </div>
          {!compact && <div style={{ fontSize:14, color:'var(--muted)', marginTop:6 }}>No signup. Your numbers stay on your device.</div>}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:14 }}>
          {ids.map((id) => {
            const a = REDE_APPS[id];
            return (
              <a key={id} href={redeAppUrl(id, agentSlug)} target="_blank" rel="noopener noreferrer" style={tile}>
                {!compact && <div style={{ fontSize:20, marginBottom:8 }}>{a.icon}</div>}
                <div style={{ fontSize: compact ? 15 : 16, fontWeight:800, color:'var(--text)', marginBottom:5 }}>
                  {a.name}{compact && <span style={{ color:'var(--muted)', fontWeight:600, fontSize:13 }}> →</span>}
                </div>
                <div style={{ fontSize:13.5, color:'var(--muted)', lineHeight:1.55, marginBottom: compact ? 0 : 11 }}>
                  {compact ? a.short : a.blurb}
                </div>
                {!compact && <div style={{ fontSize:12.5, fontWeight:700, color:'var(--accent)' }}>{`Open ${a.name} →`}</div>}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* Copy Link — the same share card the other client apps carry. The link is
   read from the current URL, so an agent-slugged page shares the AGENT's
   link, not the house one. */
function ShareCard() {
  return (
    <section style={{ padding:'34px 24px 6px' }}>
      <div style={{ maxWidth:640, margin:'0 auto', textAlign:'center', background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:32 }}>
        <div style={{ fontSize:23, fontWeight:800, color:'var(--text)' }}>Know someone who could use this?</div>
        <p style={{ fontSize:15, color:'var(--muted)', margin:'8px auto 18px', maxWidth:460, lineHeight:1.6 }}>
          Share PropertyPath with someone deciding what to do with a property they already own — copy the link and send it any way you like.
        </p>
        <button
          onClick={(e) => {
            const btn = e.currentTarget;
            const link = window.location.origin + window.location.pathname.replace(/\/(app)?$/, '');
            if (navigator.clipboard) navigator.clipboard.writeText(link).then(() => {
              btn.textContent = 'Copied!';
              setTimeout(() => { btn.textContent = 'Copy Link'; }, 1600);
            });
          }}
          style={{ background:'var(--gold)', color:'#fff', border:'none', borderRadius:8, padding:'15px 34px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}
        >Copy Link</button>
      </div>
    </section>
  );
}

export default function App() {
  const { mode, slug } = detectRoute();
  const isDirectApp = mode === 'app';
  const isAgentLanding = mode === 'agent';
  const isLanding = mode === 'landing' || isAgentLanding;

  // REDE Brand
  const [brand, setBrand] = useState(null);
  const [brandLoading, setBrandLoading] = useState(!!slug);
  const [brandError, setBrandError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setBrandLoading(true);
    fetch(`/api/brand/${slug}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setBrandError(data.error); setBrandLoading(false); return; }
        setBrand(data);
        setBrandPayload(data);
        // Same fallthrough rule as the gold slot below, priority reversed:
        // an agent who supplied only one of the two colors must never render
        // the house green.
        const _accVar = brandHex(data.brand_identity?.primary_color,
                          brandHex(data.brand_identity?.accent_color, ''));
        if (_accVar) {
          document.documentElement.style.setProperty('--accent', _accVar);
        }
        // Validate before use: these values get alpha suffixes concatenated
        // onto them below, and a short/garbage value emits invalid CSS.
        const _goldVar = brandHex(data.brand_identity?.accent_color,
                          brandHex(data.brand_identity?.primary_color, ''));
        if (_goldVar) {
          const _ds = document.documentElement.style;
          _ds.setProperty('--gold', _goldVar);
          _ds.setProperty('--gold-bright', _goldVar);
          _ds.setProperty('--gold-subtle', _goldVar + '14');
          _ds.setProperty('--gold-border', _goldVar + '33');
        }
        setBrandLoading(false);
      })
      .catch(err => { setBrandError(err.message); setBrandLoading(false); });
  }, [slug]);

  // Theme
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem('rede-theme');
      if (saved) return saved === 'dark';
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return false;
    } catch (_e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('rede-theme', dark ? 'dark' : 'light'); } catch (_e) {}
  }, [dark]);

  // App state — /app starts at questionnaire, / and /slug start at landing
  // ?view=app (v4.0.1): dashboard "Open app" deep link. Returning users
  // (saved properties) land on their dashboard via the existing ?load=latest
  // mechanism; first-timers land in the questionnaire. Deterministic:
  // saved work -> dashboard, no saved work -> new analysis.
  const wantApp = new URLSearchParams(window.location.search).get('view') === 'app';
  const hasSavedProps = (() => { try { return JSON.parse(localStorage.getItem('vhg-saved-properties')||'[]').length > 0; } catch(_e) { return false; } })();
  const [view, setView] = useState(() => (isDirectApp || wantApp) ? 'questionnaire' : 'landing');
  const legalOrigin = useRef('landing');
  useEffect(() => {
    if (wantApp && hasSavedProps && !new URLSearchParams(window.location.search).get('load')) {
      const u = new URL(window.location.href); u.searchParams.set('load','latest');
      window.history.replaceState({}, '', u.toString());
      window.location.reload();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [rawFormData, setRawFormData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [holdResult, setHoldResult] = useState(null);
  const [sellResult, setSellResult] = useState(null);
  const [exchangeResult, setExchangeResult] = useState(null);
  const [isPro, setIsPro] = useState(() => {
    try { return !!localStorage.getItem('vhg-pro-email'); } catch(_e) { return false; }
  });
  const [proUserEmail, setProUserEmail] = useState(() => {
    try { return localStorage.getItem('vhg-pro-email')||''; } catch(_e) { return ''; }
  });
  const [showProGate, setShowProGate] = useState(false);

  const calcRef = useRef(null);
  const featRef = useRef(null);
  const howRef = useRef(null);
  const proRef = useRef(null);
  const docsRef = useRef(null);

  // Run analysis — infers discovery from form data
  const handleAnalyze = useCallback((data) => {
    const inferred = inferDiscovery(data);
    setDiscoveryData(inferred);
    const isBuyerMode = data.ownsProperty === 'no';
    const n = {
      ...data,
      vacancyRate: parseFloat(data.vacancyRate)||10,
      mortgageRate: (parseFloat(data.mortgageRate)||0)/100,
      annualAppreciation: (parseFloat(data.annualAppreciation)||3)/100,
      alternativeReturn: (parseFloat(data.alternativeReturn)||7)/100,
      taxBracket: (parseFloat(data.taxBracket)||32)/100,
      sellingCostsPct: parseFloat(data.sellingCostsPct)||(isBuyerMode?3:7.5),
      isBuyer: isBuyerMode,
    };
    setRawFormData(data);
    setFormData(n);
    setHoldResult(calculateHoldScenario(n, 10));
    setSellResult(calculateSellScenario(n, 10, n.alternativeReturn));
    setExchangeResult(data.exitStrategy === '1031' ? calculate1031Scenario(n, 10) : null);
    setView('dashboard');
    window.scrollTo({top:0,behavior:'smooth'});
  }, []);

  // Load shared/saved analysis from URL params
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('load') === 'latest') {
        const saved = JSON.parse(localStorage.getItem('vhg-saved-properties')||'[]');
        if (saved.length > 0) {
          const latest = saved[saved.length - 1];
          setTimeout(() => handleAnalyze(latest.formData), 100);
        }
        return;
      }
      const compact = params.get('s');
      if (compact) {
        const keyMap = {t:'propertyType',l:'location',pp:'purchasePrice',cv:'currentValue',yo:'yearsOwned',ar:'annualRent',ae:'annualExpenses',vr:'vacancyRate',mb:'mortgageBalance',mr:'mortgageRate',my:'mortgageYearsRemaining',ra:'roofAge',ha:'hvacAge',wa:'waterHeaterAge',ap:'annualAppreciation',rt:'alternativeReturn',es:'exitStrategy',tb:'taxBracket',sc:'sellingCostsPct',ms:'managementStyle',ai:'alternativeInvestment',rv:'replacementValue',rr:'replacementRent',re:'replacementExpenses',cr:'capRate',op:'ownsProperty'};
        const defaults = {propertyType:'single-family',managementStyle:'self-managed',vacancyRate:'10',roofAge:'5',hvacAge:'5',waterHeaterAge:'3',annualAppreciation:'3',alternativeInvestment:'stock-market',alternativeReturn:'7',exitStrategy:'undecided',taxBracket:'32',sellingCostsPct:'7.5',ownsProperty:'yes'};
        const parsed = JSON.parse(atob(compact + '=='.slice(0, (4 - compact.length % 4) % 4)));
        const fd = {...defaults};
        Object.entries(parsed).forEach(([k,v]) => { if(keyMap[k]) fd[keyMap[k]] = v; });
        setTimeout(() => handleAnalyze(fd), 100);
        return;
      }
      const shared = params.get('share');
      if (shared) {
        const data = JSON.parse(decodeURIComponent(atob(shared)));
        if (data.formData) {
          setTimeout(() => handleAnalyze(data.formData), 100);
        }
      }
    } catch (_e) {}
  }, []); // eslint-disable-line

  // Navigation handler
  const handleNav = (target) => {
    if (target === 'landing') {
      if (isDirectApp) { window.location.href = slug ? '/' + slug : '/'; return; }
      setView('landing'); window.scrollTo({top:0,behavior:'smooth'});
    }
    else if (target === 'calculator') {
      if (!isDirectApp && !slug && view === 'landing') { window.location.href = '/app'; return; }
      setView('questionnaire');
    }
    else if (target === 'features') { if(isDirectApp){window.location.href='/#features';return;} if(view!=='landing') setView('landing'); setTimeout(()=>featRef.current?.scrollIntoView({behavior:'smooth'}),100); }
    else if (target === 'howItWorks') { if(isDirectApp){window.location.href='/#how-it-works';return;} if(view!=='landing') setView('landing'); setTimeout(()=>howRef.current?.scrollIntoView({behavior:'smooth'}),100); }
    else if (target === 'pro') { if(isDirectApp){window.location.href='/#pro';return;} if(view!=='landing') setView('landing'); setTimeout(()=>proRef.current?.scrollIntoView({behavior:'smooth'}),100); }
    else if (target === 'resources') { if(isDirectApp){window.location.href='/#resources';return;} if(view!=='landing') setView('landing'); setTimeout(()=>docsRef.current?.scrollIntoView({behavior:'smooth'}),100); }
    else if (target === 'privacy') { if (view !== 'privacy' && view !== 'terms') legalOrigin.current = view; setView('privacy'); window.scrollTo({top:0,behavior:'smooth'}); }
    else if (target === 'terms') { if (view !== 'privacy' && view !== 'terms') legalOrigin.current = view; setView('terms'); window.scrollTo({top:0,behavior:'smooth'}); }
  };

  const themeVars = { ...(dark ? darkVars : lightVars) };
  // Agent brand colors win over the default theme vars (these are spread onto the
  // wrapper below, which otherwise shadows the vars set on documentElement).
  const _biTheme = brand?.brand_identity;
  const _accTheme = brandHex(_biTheme?.primary_color, brandHex(_biTheme?.accent_color, ''));
  if (_accTheme) themeVars['--accent'] = _accTheme;
  const _goldTheme = brandHex(_biTheme?.accent_color, brandHex(_biTheme?.primary_color, ''));
  if (_goldTheme) {
    themeVars['--gold'] = _goldTheme;
    themeVars['--gold-bright'] = _goldTheme;
    themeVars['--gold-subtle'] = _goldTheme + '14';
    themeVars['--gold-border'] = _goldTheme + '33';
  }

  // Brand loading state
  if (brandLoading) {
    return (
      <div style={{...themeVars, background:'var(--bg)', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center'}}>
        <div style={{textAlign:'center',color:'var(--muted)'}}>
          <div style={{fontSize:20,fontWeight:700,color:'var(--text)',marginBottom:8}}>Property<span style={{color:'var(--gold)'}}>Path</span></div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (brandError && mode === 'agent') {
    // Fall back to default branding — don't block the app
    console.warn('Brand API error for slug:', slug, brandError);
    // Continue rendering with brand=null (VHG defaults)
  }

  return (
    <div style={{
      ...themeVars,
      background: 'var(--bg)',
      color: 'var(--text)',
      minHeight: '100vh',
      fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    }}>
      {view === 'landing' && <NavBar dark={dark} setDark={setDark} onNav={handleNav} brand={brand}/>}

      {view === 'landing' && (
        <LandingPage
          brandData={brand}
          isPro={isPro}
          onOpenCalc={() => { slug ? setView('questionnaire') : window.location.href = '/app'; }}
          onStartDiscovery={() => { slug ? setView('questionnaire') : window.location.href = '/app'; }}
          onLoadSaved={() => {
            if (slug) {
              try {
                const saved = JSON.parse(localStorage.getItem('vhg-saved-properties')||'[]');
                if (saved.length > 0) handleAnalyze(saved[saved.length - 1].formData);
              } catch(_e) {}
            } else {
              window.location.href = '/app?load=latest';
            }
          }}
          onProClick={() => setShowProGate(true)}
          featRef={featRef}
          howRef={howRef}
          proRef={proRef}
          docsRef={docsRef}
          brand={brand}
        />
      )}

      {view === 'questionnaire' && (
        <div ref={calcRef}>
          <Questionnaire onComplete={handleAnalyze} initialData={rawFormData} dark={dark}/>
        </div>
      )}

      {view === 'dashboard' && holdResult && sellResult && (
        <Dashboard
          formData={formData}
          rawFormData={rawFormData}
          holdResult={holdResult}
          sellResult={sellResult}
          exchangeResult={exchangeResult}
          onEditAssumptions={() => setView('questionnaire')}
          onNewProperty={() => { try{localStorage.removeItem('pp-autosave');}catch(e){} setRawFormData(null); setFormData(null); setHoldResult(null); setSellResult(null); setExchangeResult(null); setView('questionnaire'); window.scrollTo({top:0,behavior:'smooth'}); }}
          onLoadProperty={(savedFormData) => handleAnalyze(savedFormData)}
          dark={dark}
          setDark={setDark}
          isPro={isPro}
          onProClick={() => setShowProGate(true)}
          discoveryData={discoveryData}
          proUserEmail={proUserEmail}
          brand={brand}
          slug={slug}
        />
      )}

      {(view === 'privacy' || view === 'terms') && <NavBar dark={dark} setDark={setDark} onNav={handleNav} brand={brand}/>}
      {view === 'privacy' && <PrivacyPolicy brand={brand} onBack={() => { setView(legalOrigin.current || 'landing'); window.scrollTo({top:0,behavior:'smooth'}); }}/>}
      {view === 'terms' && <TermsOfService brand={brand} onBack={() => { setView(legalOrigin.current || 'landing'); window.scrollTo({top:0,behavior:'smooth'}); }}/>}
      {view === 'landing' && <ShareCard />}
      {view === 'landing' && <CrossPromo targets={['sellercalc','strcalc']} brandData={brand} agentSlug={slug} />}
      {view === 'dashboard' && <CrossPromo targets={['sellercalc','strcalc']} brandData={brand} agentSlug={slug} compact />}

      <VHGFooter dark={dark} brand={brand} onNav={handleNav}/>

      {showProGate && (
        <ProGate
          onUnlock={(user) => {
            setIsPro(true);
            setProUserEmail(user.email||'');
            setShowProGate(false);
            try{localStorage.setItem('vhg-pro-email',user.email||'pro');localStorage.setItem('vhg-pro-name',user.name||'');}catch(_e){}
          }}
          onClose={() => setShowProGate(false)}
          discoveryData={discoveryData}
          slug={slug}
        />
      )}
    </div>
  );
}
